import { Shell } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
	FieldSet,
} from "@/components/ui/field";
import { useForm } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
	InputGroupText,
} from "@/components/ui/input-group";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { GlobeIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

async function createApplication(
	name: string,
	website: string | undefined,
): Promise<{ id: string }> {
	const res = await fetch("/api/applications", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify({ name, website }),
	});
	if (!res.ok) throw new Error("Failed to create application");
	return res.json() as Promise<{ id: string }>;
}

const schema = z.object({
	name: z
		.string("Name is required")
		.min(1, "Name is required")
		.max(255, "Name is too long"),
	website: z
		.string()
		.regex(
			/^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/,
			"URL is invalid",
		)
		.or(z.literal(""))
		.optional(),
});

export const NewApplication = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: ({
			name,
			website,
		}: {
			name: string;
			website: string | undefined;
		}) => createApplication(name, website),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ["applications"] });
			navigate(`/${data.id}`, { replace: true });
		},
	});

	const form = useForm<z.infer<typeof schema>>({
		validators: {
			onSubmit: schema,
		},
		defaultValues: { name: "", website: undefined },
		onSubmit: async ({ value }) => {
			await mutation.mutateAsync({ name: value.name, website: value.website });
		},
	});

	return (
		<Shell>
			<Card>
				<CardHeader>
					<CardTitle>New application</CardTitle>
					<CardDescription>
						Create an application to group and monitor services.
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
						id="new-application"
					>
						<FieldGroup>
							<FieldSet>
								<form.Field name="name">
									{(field) => (
										<Field>
											<FieldLabel htmlFor="name">Name</FieldLabel>
											<Input
												id="name"
												value={field.state.value}
												onChange={(e) => field.handleChange(e.target.value)}
												onBlur={field.handleBlur}
												placeholder="My App"
												disabled={mutation.isPending}
											/>
											<FieldError errors={field.state.meta.errors} />
										</Field>
									)}
								</form.Field>
								<form.Field name="website">
									{(field) => (
										<Field>
											<FieldLabel htmlFor="name">Website</FieldLabel>
											<InputGroup>
												<InputGroupAddon>
													<GlobeIcon />
													<InputGroupText>https://</InputGroupText>
												</InputGroupAddon>
												<InputGroupInput
													id="website"
													value={field.state.value ?? ""}
													onChange={(e) => field.handleChange(e.target.value)}
													onBlur={field.handleBlur}
													placeholder="example.com"
													className="pl-0!"
													disabled={mutation.isPending}
												/>
											</InputGroup>
											<FieldError errors={field.state.meta.errors} />
										</Field>
									)}
								</form.Field>
							</FieldSet>
						</FieldGroup>
					</form>
				</CardContent>
				<CardFooter className="grid">
					<form.Subscribe
						selector={(state) => [
							state.isSubmitting,
							state.isValid,
							state.isDirty,
						]}
					>
						{([isSubmitting, isValid, isDirty]) => (
							<Button
								type="submit"
								form="new-application"
								disabled={isSubmitting || !isValid || !isDirty}
							>
								{isSubmitting ? "Creating…" : "Create"}
							</Button>
						)}
					</form.Subscribe>
				</CardFooter>
			</Card>
		</Shell>
	);
};
